import React, { useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/pt_logo_trans.png";

const ArrowRight = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path
      d="M1 7h12m-4-4 4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function Shield({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const height = size === "lg" ? 72 : size === "sm" ? 40 : 56;
  return (
    <Link
      to="/"
      style={{
        display: "inline-flex",
        alignItems: "center",
        textDecoration: "none",
      }}
    >
      <img
        src={logo}
        alt="Preferred Title & Escrow"
        style={{ height, width: "auto" }}
      />
    </Link>
  );
}

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    {
      label: "Services",
      href: "/#our-services",
      external: false,
      routerLink: false,
    },
    { label: "Our Team", href: "/our-team", external: false, routerLink: true },
    {
      label: "Resources",
      href: "https://fntic.com/whatistitle.aspx",
      external: true,
      routerLink: false,
    },
    { label: "Contact", href: "/contact", external: false, routerLink: true },
  ];

  const linkStyle: React.CSSProperties = {
    fontSize: 14,
    color: "var(--ink)",
    fontWeight: 500,
    textDecoration: "none",
  };

  return (
    <>
      {/* Announce bar */}
      <div
        style={{
          background: "var(--gold)",
          color: "var(--maroon-deep)",
          fontSize: 13,
          padding: "10px 0",
          textAlign: "center",
          fontWeight: 500,
        }}
      >
        <span
          style={{
            fontFamily: "var(--mono)",
            fontSize: 11,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            marginRight: 12,
            opacity: 0.75,
          }}
        >
          News
        </span>
        Now accepting applications for the Greenbelt Homes Inc. Grant —{" "}
        <a
          href="https://www.greenbeltmd.gov/209/Greenbelt-Homes-Inc-First-Time-Homebuyer"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: "underline",
            textUnderlineOffset: 3,
            color: "inherit",
          }}
        >
          apply here
        </a>
      </div>

      {/* Sticky nav */}
      <header className="pte-site-header">
        <Shield />

        {/* Desktop nav */}
        <nav className="pte-desktop-nav">
          {navLinks.map((item) =>
            item.routerLink ? (
              <Link key={item.label} to={item.href} style={linkStyle}>
                {item.label}
              </Link>
            ) : (
              <a
                key={item.label}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                style={linkStyle}
              >
                {item.label}
              </a>
            ),
          )}
          <a
            href="tel:3015139090"
            style={{
              fontSize: 14,
              color: "var(--ink-soft)",
              fontFamily: "var(--mono)",
              letterSpacing: "0.05em",
              textDecoration: "none",
            }}
          >
            (301) 513-9090
          </a>
          <a
            href="https://preferredtitle.titlecapture.com/title-quote"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-dark"
            style={{ padding: "10px 20px", fontSize: 14 }}
          >
            Get a quote <ArrowRight />
          </a>
        </nav>

        {/* Mobile hamburger */}
        <button
          className="pte-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
            {menuOpen ? (
              <path
                d="M1 1l12 8M13 1L1 9"
                stroke="var(--maroon)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            ) : (
              <path
                d="M1 1h12M1 5h12M1 9h12"
                stroke="var(--maroon)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            )}
          </svg>
        </button>
      </header>

      {/* Mobile menu — full-screen overlay */}
      {menuOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "var(--paper)",
            zIndex: 200,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          {/* Overlay header row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 20px",
              borderBottom: "1px solid var(--line)",
              flexShrink: 0,
            }}
          >
            <Shield size="sm" />
            <button
              onClick={() => setMenuOpen(false)}
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                border: "1px solid var(--line)",
                background: "var(--paper)",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
              }}
              aria-label="Close menu"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M1 1l10 10M11 1L1 11"
                  stroke="var(--maroon)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Nav links */}
          <nav
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "8px 20px 0",
            }}
          >
            {navLinks.map((item) => {
              const mobileStyle: React.CSSProperties = {
                padding: "18px 0",
                fontSize: 24,
                fontFamily: "var(--serif)",
                color: "var(--maroon)",
                borderBottom: "1px solid var(--line)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                textDecoration: "none",
              };
              return item.routerLink ? (
                <Link
                  key={item.label}
                  to={item.href}
                  style={mobileStyle}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label} <ArrowRight size={16} />
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  style={mobileStyle}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label} <ArrowRight size={16} />
                </a>
              );
            })}
          </nav>

          {/* Bottom contact */}
          <div style={{ padding: "24px 20px", marginTop: "auto" }}>
            <a
              href="tel:3015139090"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontFamily: "var(--mono)",
                fontSize: 14,
                color: "var(--ink-soft)",
                letterSpacing: "0.05em",
                textDecoration: "none",
                marginBottom: 16,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#3da361",
                  flexShrink: 0,
                }}
              />
              (301) 513-9090
            </a>
            <a
              href="https://preferredtitle.titlecapture.com/title-quote"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-dark"
              style={{ width: "100%", justifyContent: "center" }}
            >
              Get a quote <ArrowRight />
            </a>
          </div>
        </div>
      )}
    </>
  );
}
